<script lang="ts">
    import type {GridField, RecordRow} from 'src/features/record-grid/lib/types';
    import type {LogbookReportPairingType, PairUnpairEventData} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/types';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {tooltip} from 'shared/ui-widgets/tooltip/use.tooltip';
    import {getTextFromGridFieldValue, isGridFieldValueEmpty} from 'src/features/erp-sutor/sutor-utils';
    import {createEventDispatcher} from 'svelte';
    import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import KpUserAvatar from 'shared/components/kp-user-avatar/KpUserAvatar.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';

    export let record: RecordRow;
    export let pairingType: LogbookReportPairingType;
    export let isProcessing: boolean;

    const dispatch = createEventDispatcher<{'pair-unpair': PairUnpairEventData}>();

    const dateField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.dateFieldId);
    const workerField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.workerFieldId);
    const machineField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.machineFieldId);
    const nameField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.nameFieldId);

    $: reportedValueStrings = getReportedValue(record);

    function getReportedValue(row: RecordRow): {value: string, tooltipLabel: string} {
        const reportedPersonStandardField = getFirstFieldByFieldTypeIdRecursive(row, FOND_REPORT.reportedPersonStandardFieldId);
        const reportedPersonOvertimeField = getFirstFieldByFieldTypeIdRecursive(row, FOND_REPORT.reportedPersonOvertimeFieldId);
        const reportedMachineField = getFirstFieldByFieldTypeIdRecursive(row, FOND_REPORT.reportedMachineFieldId);
        const personUnitField = getFirstFieldByFieldTypeIdRecursive(row, FOND_REPORT.personUnitFieldId);
        const machineUnitField = getFirstFieldByFieldTypeIdRecursive(row, FOND_REPORT.machineUnitFieldId);

        const values: {valueField: GridField, unitField: GridField | null, tooltipLabel: string}[] = [];

        if (!isGridFieldValueEmpty(reportedPersonStandardField)) {
            values.push({
                valueField: reportedPersonStandardField,
                unitField: personUnitField,
                tooltipLabel: 'Vykázáno pracovník'
            });
        }

        if (!isGridFieldValueEmpty(reportedPersonOvertimeField)) {
            values.push({
                valueField: reportedPersonOvertimeField,
                unitField: personUnitField,
                tooltipLabel: 'Přesčas pracovník'
            });
        }

        if (!isGridFieldValueEmpty(reportedMachineField)) {
            values.push({
                valueField: reportedMachineField,
                unitField: machineUnitField,
                tooltipLabel: 'Vykázáno stroj'
            });
        }

        return {
            value: values.map((value) => {
                if (!exists(value.unitField)) {
                    return getTextFromGridFieldValue(value.valueField);
                }

                return `${getTextFromGridFieldValue(value.valueField)} ${getTextFromGridFieldValue(value.unitField)}`;
            }).join(' | '),
            tooltipLabel: values.map((value) => value.tooltipLabel).join(' | ')
        };
    }

    const handlePairUnpairClick = () => {
        dispatch('pair-unpair', {record, pairingType});
    };
</script>

{#if pairingType === 'pair'}
    <td>
        <KpButton isLoading="{isProcessing}" noWrap buttonSize="xs" buttonStyle="success-new" on:click={handlePairUnpairClick}>
            <IconedContent icon="arrow-left">
                Spárovat
            </IconedContent>
        </KpButton>
    </td>
{/if}

<td>
    <div class="worker-or-machine-container">
        {#if exists(workerField)}
            <GridFieldValue field="{workerField}">
                <KpUserAvatar userRecordId="{workerField?.recordReference?.id}" sizePx="{24}"/>
                {getTextFromGridFieldValue(workerField)}
            </GridFieldValue>
        {/if}

        {#if exists(machineField)}
            <GridFieldValue field="{machineField}">
                <IconedContent icon="gears">
                    {getTextFromGridFieldValue(machineField)}
                </IconedContent>
            </GridFieldValue>
        {/if}
    </div>
</td>

<td>
    <GridFieldValue field="{dateField}"/>
</td>

<td>
    <div class="reported-value-container" use:tooltip={{enabled: true, content: reportedValueStrings.tooltipLabel, role: 'tooltip'}}>
        {reportedValueStrings.value}
    </div>
</td>

<td>
    <GridFieldValue field="{nameField}"/>
</td>

<td>
    {pipe(record.fond, loc())}
</td>

{#if pairingType === 'unpair'}
    <td>
        <KpButton isLoading="{isProcessing}" noWrap buttonSize="xs" buttonStyle="danger-new" on:click={handlePairUnpairClick}>
            <IconedContent trailingIcon="arrow-right">
                Odpárovat
            </IconedContent>
        </KpButton>
    </td>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .worker-or-machine-container {
        display: inline-flex;
        flex-direction: column;
        gap: @spacing-xs;
    }

    .reported-value-container {
        align-items: center;
        width: min-content;
        white-space: nowrap;
        gap: @spacing-s;
        min-width: 100px;
        display: flex;
        justify-content: space-between;
    }
</style>